import { useState } from "react";
import { Routes, Route, NavLink, useNavigate, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DashboardThemeProvider, ThemeToggleButton } from "@/context/DashboardTheme";
import VetLogin from "@/pages/vet/VetLogin";
import VetDashboard from "@/pages/vet/VetDashboard";
import VetSettings from './VetSettings';
import VetReview from "@/pages/vet/VetReview";
import { Toaster } from "@/components/ui/sonner";
import { Stethoscope, ClipboardList, Settings, LogOut, Loader2, ShieldCheck, Menu, X } from "lucide-react";

function VetShell() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (loading) return <div className="min-h-screen bg-[#0e1812] flex items-center justify-center"><Loader2 className="w-8 h-8 text-emerald-400/40 animate-spin" /></div>;
  if (!user || user === false || user.role !== "vet") return <VetLogin onLogin={() => navigate("/vet", { replace: true })} />;

  const handleLogout = async () => { await logout(); navigate("/vet", { replace: true }); };

  return (
    <div className="min-h-screen bg-paw-bg dark:bg-[#0e1812] text-paw-text dark:text-white flex" data-pawlinx-app="vet" data-testid="vet-layout">
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-white dark:bg-[#142318] border-b border-paw-border dark:border-white/5 flex items-center justify-between px-4">
        <button onClick={() => setOpen(true)} className="p-2 -ml-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5" data-testid="vet-menu-btn"><Menu className="w-5 h-5" /></button>
        <div className="flex items-center gap-2">
          <img src="/logo-light-256.png" alt="PawLinx" className="h-9 w-auto dark:hidden" data-testid="pawlinx-logo" />
          <img src="/logo-dark-256.png" alt="PawLinx" className="h-9 w-auto hidden dark:block" data-testid="pawlinx-logo-dark" />
          <span className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Vet</span>
        </div>
        <ThemeToggleButton />
      </header>

      {open && <div className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-[#142318] border-r border-paw-border dark:border-white/5 flex flex-col transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="vet-sidebar">
        <div className="px-6 py-6 border-b border-paw-border dark:border-white/5 flex items-center justify-between">
          <div>
            <img src="/logo-light-256.png" alt="PawLinx" className="h-12 w-auto dark:hidden" data-testid="pawlinx-logo" />
            <img src="/logo-dark-256.png" alt="PawLinx" className="h-12 w-auto hidden dark:block" data-testid="pawlinx-logo-dark" />
            <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40 mt-1.5 flex items-center gap-1.5"><Stethoscope className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" strokeWidth={1.5} /> Vet Portal</p>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden p-1 rounded hover:bg-black/5 dark:hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          <NavLink to="/vet" end onClick={() => setOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg font-manrope text-sm transition-colors ${isActive ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" : "text-paw-text-secondary dark:text-white/60 hover:text-paw-text dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5"}`} data-testid="vet-nav-queue">
            <ClipboardList className="w-4 h-4" strokeWidth={1.5} /> Review Queue
          </NavLink>
          <NavLink to="/vet/settings" end onClick={() => setOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg font-manrope text-sm transition-colors ${isActive ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" : "text-paw-text-secondary dark:text-white/60 hover:text-paw-text dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5"}`} data-testid="vet-nav-settings">
            <Settings className="w-4 h-4" strokeWidth={1.5} /> Settings
          </NavLink>
        </nav>
        <div className="p-3 border-t border-paw-border dark:border-white/5 space-y-2">
          <ThemeToggleButton className="w-full justify-start hidden lg:flex" />
          <div className="px-3 py-2">
            <p className="font-manrope text-xs text-paw-text-muted dark:text-white/40">Signed in as</p>
            <p className="font-manrope text-sm text-paw-text dark:text-white/80 truncate">Dr. {user.first_name} {user.last_name}</p>
            {user.license_verified ? <p className="font-manrope text-xs text-emerald-600 dark:text-emerald-400 mt-0.5 flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> License verified</p> : <p className="font-manrope text-xs text-amber-500 dark:text-amber-400 mt-0.5">License pending</p>}
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-manrope text-paw-text-secondary dark:text-white/60 hover:text-paw-text dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 transition-colors" data-testid="vet-logout">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden pt-14 lg:pt-0">
        <Routes>
          <Route path="/" element={<VetDashboard />} />
          <Route path="/vet" element={<VetDashboard />} />
          <Route path="/vet/order/:orderId" element={<VetReview />} />
          <Route path="/vet/settings" element={<VetSettings />} />
          <Route path="/vet/*" element={<Navigate to="/vet" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export default function VetLayout() {
  return (
    <DashboardThemeProvider storageKey="pawlinx-vet-theme">
      <AuthProvider>
        <VetShell />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </DashboardThemeProvider>
  );
}
